/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {DesktopHandoffInfoResponse} from '@app/actions/AuthenticationActionCreators';
import * as AuthenticationActionCreators from '@app/actions/AuthenticationActionCreators';
import {useCallback, useMemo, useRef, useState} from 'react';

export type DesktopHandoffMode =
	| 'idle'
	| 'selecting'
	| 'login'
	| 'warning'
	| 'code_input'
	| 'fetching_info'
	| 'approving'
	| 'completing'
	| 'done'
	| 'error';

const APPROVAL_FLOW_MODES: ReadonlySet<DesktopHandoffMode> = new Set([
	'warning',
	'code_input',
	'fetching_info',
	'approving',
	'completing',
	'done',
	'error',
]);

export function isApprovalFlowMode(mode: DesktopHandoffMode): boolean {
	return APPROVAL_FLOW_MODES.has(mode);
}

interface Options {
	enabled: boolean;
	hasStoredAccounts: boolean;
	initialMode?: DesktopHandoffMode;
}

export function useDesktopHandoffFlow({enabled, hasStoredAccounts, initialMode}: Options) {
	const derivedInitial = useMemo<DesktopHandoffMode>(() => {
		if (!enabled) return 'idle';
		if (initialMode) return initialMode;
		return hasStoredAccounts ? 'selecting' : 'login';
	}, [enabled, hasStoredAccounts, initialMode]);

	const [mode, setMode] = useState<DesktopHandoffMode>(derivedInitial);
	const [error, setError] = useState<string | null>(null);
	const [clientInfo, setClientInfo] = useState<DesktopHandoffInfoResponse['client_info']>(null);
	const [handoffCode, setHandoffCode] = useState<string | null>(null);

	const tokenRef = useRef<string | null>(null);
	const userIdRef = useRef<string | null>(null);

	const start = useCallback(
		({token, userId}: {token: string; userId: string}) => {
			if (!enabled) return;
			tokenRef.current = token;
			userIdRef.current = userId;
			setError(null);
			setHandoffCode(null);
			setClientInfo(null);
			setMode('warning');
		},
		[enabled],
	);

	const proceedToCodeInput = useCallback(() => {
		setMode('code_input');
		setError(null);
	}, []);

	const submitCode = useCallback(async (code: string) => {
		setMode('fetching_info');
		setError(null);
		setHandoffCode(code);

		try {
			const info = await AuthenticationActionCreators.fetchDesktopHandoffInfo(code);

			if (info.status === 'expired') {
				setMode('error');
				setError('Invalid or expired code. Please try again.');
				return;
			}

			setClientInfo(info.client_info);
			setMode('approving');
		} catch (e) {
			setMode('error');
			setError(e instanceof Error ? e.message : String(e));
		}
	}, []);

	const approve = useCallback(async () => {
		if (!handoffCode || !tokenRef.current || !userIdRef.current) return;

		setMode('completing');
		setError(null);

		try {
			await AuthenticationActionCreators.completeDesktopHandoff({
				code: handoffCode,
				token: tokenRef.current,
				userId: userIdRef.current,
			});
			setMode('done');
		} catch (e) {
			setMode('error');
			setError(e instanceof Error ? e.message : String(e));
		}
	}, [handoffCode]);

	const deny = useCallback(() => {
		setHandoffCode(null);
		setClientInfo(null);
		setMode('code_input');
	}, []);

	const switchToLogin = useCallback(() => {
		setMode('login');
		setError(null);
	}, []);

	const retry = useCallback(() => {
		setError(null);
		setHandoffCode(null);
		setClientInfo(null);
		tokenRef.current = null;
		userIdRef.current = null;
		setMode(hasStoredAccounts ? 'selecting' : 'login');
	}, [hasStoredAccounts]);

	return {
		mode,
		error,
		clientInfo,
		handoffCode,

		setMode,

		start,
		proceedToCodeInput,
		submitCode,
		approve,
		deny,
		switchToLogin,
		retry,
	};
}
